import { useEffect, useState } from "react";
import { Loader2, PlugZap, RefreshCw, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  selectConnection,
  selectReconnectAttempt,
  selectIsDesynced,
  selectIsTerminal,
  selectStreamError,
  useRunStateStore,
  type StreamConnection,
} from "@/stores/runStateStore";

/**
 * The connection's standing, and the recovery path when it needs one (§11, §12).
 *
 * FOUR STATES, THREE VOLUMES, and the volumes are the design.
 *
 *   desynced       a quiet chip. Recovery is automatic and reliable — the store makes desync sticky,
 *                  the stream hook re-reads `GET /state`, and `applySnapshot` clears the flag. A red
 *                  banner for a self-healing sub-second event teaches people to distrust a system
 *                  that is working. §12 asks for calm, and calm is the honest register here.
 *   reconnecting   a quiet chip too. `subscribeToRun` backs off and retries up to ten times; nothing
 *                  is lost in between, because the server replays from `Last-Event-ID`.
 *   disconnected   a real banner WITH a retry, because this is the state the user must act on: the
 *                  reconnect loop is exhausted and no more frames are coming on their own.
 *   connected      nothing at all. A green "connected" pill is a permanent reassurance that trains
 *                  the eye to skip the strip, which is exactly where the one important state lives.
 *
 * VIEW / WIRED SPLIT, same shape as `ActionBar`/`ActionBarView`. `StreamStatusView` takes its four
 * facts as props and is what the test renders; `StreamStatus` reads the store. This is not
 * ceremony — an earlier version of this file was one connected component, and its test could only
 * assert the store's own selectors, so DELETING the terminal check below left the test green.
 * Verified by deleting it.
 *
 * WHY A TERMINAL RUN IS NOT "DISCONNECTED". `subscribeToRun` closes the stream on
 * `lifecycle.run_finished` and reports `disconnected` — correctly, the socket is gone — but the run
 * is FINISHED, not broken. Showing "connection lost" on every completed run would make success look
 * like failure, so the terminal case suppresses the banner. That is the single most important
 * condition in this file.
 */
/** How much of a transport error may reach the screen. One line's worth. */
const STREAM_ERROR_MAX = 160;

export interface StreamStatusViewProps {
  connection: StreamConnection;
  desynced: boolean;
  streamError: string | null;
  /** The run reached a terminal status. A closed stream on a finished run is not a fault. */
  terminal: boolean;
  /** A snapshot has landed. Before one, there is nothing to be stale about. */
  hasState: boolean;
  /**
   * The reconnect attempt in progress, or `null`.
   *
   * Rendered only while reconnecting and only on a live run: a counter on a terminal run would
   * describe a stream that is closed because the work FINISHED, which is not a fault to report.
   */
  reconnectAttempt?: { attempt: number; maxAttempts: number } | null;
  /** Force a `GET /state` reconcile — `useProtocolRunStream`'s `refresh`. */
  onRetry: () => Promise<void> | void;
  /**
   * The sentence under "Live updates stopped". Defaults to the run's copy; a session-keyed host
   * (lane v2) has no run to report on and passes its own — see `SESSION_STREAM_LOST_DETAIL`.
   */
  lostDetail?: string;
  className?: string;
}

const RUN_STREAM_LOST_DETAIL =
  "The connection could not be re-established. The run is still going on the server — what you see below is the last state received.";

export function StreamStatusView({
  connection,
  desynced,
  streamError,
  terminal,
  hasState,
  reconnectAttempt,
  onRetry,
  lostDetail = RUN_STREAM_LOST_DETAIL,
  className,
}: StreamStatusViewProps) {
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  /*
   * The hard state: the reconnect loop gave up, and this run is not over. `hasState` is required
   * because before the first snapshot there is nothing to be stale ABOUT — a run that has not
   * started streaming yet is "connecting", not "lost".
   */
  const lost = connection === "disconnected" && !terminal && hasState;

  if (lost) {
    return (
      <div
        role="alert"
        className={cn(
          "flex flex-wrap items-start gap-x-3 gap-y-2 rounded-md border border-ma-err-line bg-ma-err-soft px-3 py-2",
          className,
        )}
      >
        <WifiOff className="mt-[2px] h-4 w-4 shrink-0 text-ma-err-text" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-foreground">Live updates stopped</p>
          {/*
            Three facts, in the order §11 asks for: what happened, whether the work is safe, and what
            to do. The run continuing server-side is the important one — a person who thinks their
            deploy died will start a second one.
          */}
          <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
            {lostDetail}
            {/*
              CAPPED, defensively.
              
              Every current source of `streamError` is client-authored and short — `stream HTTP
              503`, `reconnect exhausted`, `frame handler failed: …` from our own reducer — and
              `getRunState` throws the STATUS without the body for exactly this reason. But this is
              the one place in the feature where a string of unbounded origin reaches primary copy,
              and §10's rule is that only 409/422 bodies may be relayed at all, length capped. A cap
              here means a future source that forwards a 5KB stack trace degrades to a clipped line
              instead of pushing the recovery button off screen.
            */}
            {streamError ? (
              <span className="block opacity-80">Reason: {streamError.slice(0, STREAM_ERROR_MAX)}</span>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void retry()}
          disabled={retrying}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-[12px] font-medium text-foreground hover:bg-muted/50 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", retrying && "animate-spin")} aria-hidden />
          {retrying ? "Reloading…" : "Reload state"}
        </button>
      </div>
    );
  }

  if (desynced) {
    return (
      <p
        /*
         * `status`, not `alert`: this is progress information, and it resolves itself. An assertive
         * region would interrupt a screen-reader user mid-sentence for something already fixed.
         */
        role="status"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground",
          className,
        )}
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Resyncing…
      </p>
    );
  }

  if (connection === "reconnecting") {
    return (
      <p
        role="status"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground",
          className,
        )}
      >
        <PlugZap className="h-3 w-3" aria-hidden />
        {/*
          The attempt counter, from `onReconnectAttempt` (a signal added for this — `onStatus`
          reports "reconnecting" several times per attempt, so folding a counter into it would
          over-count).

          SUPPRESSED ON A TERMINAL RUN. A closed stream on a finished run is not a fault, and
          "attempt 4 of 10" there reads as a failure recovering from something that already
          succeeded. `attempt` is also floored at 1: the loop increments before the wait, so an
          attempt is always in progress by the time this renders — "attempt 0" would be a number
          describing nothing.
        */}
        {reconnectAttempt && !terminal ? (
          <span className="tabular-nums">
            Reconnecting… attempt {Math.max(1, reconnectAttempt.attempt)} of{" "}
            {reconnectAttempt.maxAttempts}
          </span>
        ) : (
          "Reconnecting…"
        )}
      </p>
    );
  }

  // `connected`, `connecting` and `idle` render nothing: a permanent green pill trains the eye to
  // skip the one place the important state appears.
  return null;
}

/** Wired: reads the live store and hands the four facts to the view above. */
export interface StreamStatusProps {
  onRetry: () => Promise<void> | void;
  className?: string;
}

/**
 * How long a "reconnecting" blip has to persist before it is shown at all.
 *
 * THE BUG THIS FIXES: reported as the reconnect pill "flickering a lot" — every brief drop (a proxy
 * hiccup, a load balancer recycling an idle connection) toggles `connection` reconnecting→connected
 * for a fraction of a second, and because `StreamStatusView` returns `null` for `connected`, each
 * toggle mounted and unmounted the pill — a literal flash, and a rapid string of them reads as
 * flicker. `sse.ts`'s own backoff starts at 250ms, so a connection that recovers on the FIRST retry
 * is already back before a human eye would have registered the first frame as anything but a flash.
 *
 * 400ms is short enough that a reconnect which is actually going to take a while still shows the
 * indicator promptly, and long enough to swallow the sub-second blips entirely. This is the same
 * "flash of loading state" problem every debounced spinner solves, applied to the one state here
 * that can toggle rapidly — `disconnected` is a persistent failure (see the `lost` banner below) and
 * is shown immediately; only the transient `reconnecting` pill is debounced.
 */
const RECONNECT_DEBOUNCE_MS = 400;

export function StreamStatus({ onRetry, className }: StreamStatusProps) {
  const connection = useRunStateStore(selectConnection);
  const desynced = useRunStateStore(selectIsDesynced);
  const streamError = useRunStateStore(selectStreamError);
  const terminal = useRunStateStore(selectIsTerminal);
  const hasState = useRunStateStore((s) => s.state !== null);
  const reconnectAttempt = useRunStateStore(selectReconnectAttempt);

  const [shownConnection, setShownConnection] = useState(connection);
  useEffect(() => {
    if (connection !== "reconnecting") {
      // Every other state is real and should never be delayed — including the recovery itself,
      // which is the whole point: the pill must disappear the instant the stream is back.
      setShownConnection(connection);
      return;
    }
    const timer = setTimeout(() => setShownConnection("reconnecting"), RECONNECT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [connection]);

  return (
    <StreamStatusView
      connection={shownConnection}
      desynced={desynced}
      streamError={streamError}
      terminal={terminal}
      hasState={hasState}
      reconnectAttempt={reconnectAttempt}
      onRetry={onRetry}
      className={className}
    />
  );
}
